// src/pages/VehicleDetail.js
import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Button,
  TextField,
  Grid,
  Box,
  Paper,
  CardMedia,
  IconButton,
  Stack,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  getDocs,
  query,
  collection,
  where,
  updateDoc,
  arrayRemove,
  arrayUnion,
  deleteDoc,
} from "firebase/firestore";
import { firestore } from "../firebase";
import { getAuth } from "firebase/auth";
import { useAuthState } from "react-firebase-hooks/auth";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import {
  ref,
  uploadBytes,
  deleteObject,
  getDownloadURL,
} from "firebase/storage";
import { storage } from "../firebase";
import moment from "moment";
import VehicleCalendar from "../components/VehicleCalendar";

const DEFAULT_OPERATING_HOURS = { open: "08:00", close: "20:00" };

const VehicleDetail = () => {
  const { id: vehicleId } = useParams();
  const auth = getAuth();
  const [user, loadingAuth] = useAuthState(auth);
  const [shopSlug, setShopSlug] = useState(null);
  const navigate = useNavigate();

  const [vehicle, setVehicle] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editableVehicle, setEditableVehicle] = useState({});
  const [newExtraName, setNewExtraName] = useState("");
  const [newExtraPrice, setNewExtraPrice] = useState("");
  const [newImageFile, setNewImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    const fetchShopSlug = async () => {
      if (!user) return;
      const ownerDocRef = doc(firestore, "users", user.uid);
      const ownerSnap = await getDoc(ownerDocRef);
      if (ownerSnap.exists()) {
        setShopSlug(ownerSnap.data().shopId);
      }
    };
    fetchShopSlug();
  }, [user]);

  // Fetch vehicle details
  useEffect(() => {
    if (!shopSlug) return;
    const fetchVehicle = async () => {
      try {
        const vehicleRef = doc(
          firestore,
          "rentalShops",
          shopSlug,
          "vehicles",
          vehicleId
        );
        const docSnap = await getDoc(vehicleRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setVehicle(data);
          setEditableVehicle(data);
        }
      } catch (error) {
        console.error("Error fetching vehicle:", error);
      }
    };
    fetchVehicle();
  }, [shopSlug, vehicleId]);

  const handleDeleteVehicleClick = () => {
    if (bookings.length > 0) {
      alert(
        "This vehicle still has bookings assigned. Please reassign or remove all bookings before deletion."
      );
      return;
    }

    setDeleteDialogOpen(true);
  };

  const handleEditToggle = () => {
    setEditMode(!editMode);
  };

  const handleEditableChange = (e) => {
    const { name, value } = e.target;
    setEditableVehicle((prev) => ({ ...prev, [name]: value }));
  };

  const handlePricingChange = (e) => {
    const { name, value } = e.target;
    setEditableVehicle((prev) => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        [name]: Number(value),
      },
    }));
  };

  // Extras handling
  const handleExtraChange = (index, field, value) => {
    const updatedExtras = editableVehicle.extras?.map((extra, i) =>
      i === index ? { ...extra, [field]: value } : extra
    );
    setEditableVehicle((prev) => ({ ...prev, extras: updatedExtras }));
  };

  const handleDeleteExtra = (index) => {
    const updatedExtras = editableVehicle.extras?.filter((_, i) => i !== index);
    setEditableVehicle((prev) => ({ ...prev, extras: updatedExtras }));
  };

  const handleAddExtra = () => {
    if (!newExtraName) return;

    const id = newExtraName.toLowerCase().replace(/\s+/g, "_");
    const newExtra = {
      id,
      name: newExtraName,
      price: newExtraPrice === "" ? 0 : Number(newExtraPrice),
      optional: true,
    };

    const updatedExtras = editableVehicle.extras
      ? [...editableVehicle.extras, newExtra]
      : [newExtra];

    setEditableVehicle((prev) => ({ ...prev, extras: updatedExtras }));
    setNewExtraName("");
    setNewExtraPrice("");
  };

  const prepareVehicleDataForUpdate = (v) => ({
    plateNr: v.plateNr,
    brand: v.brand,
    model: v.model,
    description: v.description,
    deposit: Number(v.deposit),
    pricing: {
      daily: Number(v.pricing?.daily || 0),
      oneWeek: v.pricing?.oneWeek ? Number(v.pricing.oneWeek) : null,
      twoWeeks: v.pricing?.twoWeeks ? Number(v.pricing.twoWeeks) : null,
      oneMonth: v.pricing?.oneMonth ? Number(v.pricing.oneMonth) : null,
      extraHours: v.pricing?.extraHours ? Number(v.pricing.extraHours) : null,
      currency: "PHP",
    },
    extras: (v.extras ?? []).map((extra) => ({
      id: extra.id || extra.name.toLowerCase().replace(/\s+/g, "_"),
      name: extra.name,
      price: extra.price === "" ? 0 : Number(extra.price),
      optional: extra.optional ?? true,
    })),
    flags: {
      ...(v.flags || {}),
      active: v.flags?.active ?? true,
    },
    features: {
      engineSize: v.features?.engineSize || "",
      fuelType: v.features?.fuelType || "",
      seats: Number(v.features?.seats ?? 2),
      transmission: v.features?.transmission || "",
    },
  });

  // Save updated vehicle details
  const handleSave = async () => {
    try {
      const refDoc = doc(
        firestore,
        "rentalShops",
        shopSlug,
        "vehicles",
        vehicleId
      );
      await updateDoc(refDoc, prepareVehicleDataForUpdate(editableVehicle));

      setVehicle(editableVehicle);
      setEditMode(false);
    } catch (err) {
      console.error("Save error:", err);
    }
  };

  const handleImageFileChange = (e) => {
    if (e.target.files[0]) setNewImageFile(e.target.files[0]);
  };

  const handleUploadImage = async () => {
    if (!newImageFile) return;
    setUploading(true);
    try {
      const safeFileName = newImageFile.name.replace(/\s+/g, "_");
      const imagePath = `vehicleImages/${shopSlug}/${vehicleId}/${safeFileName}`;
      const storageRef = ref(storage, imagePath);
      await uploadBytes(storageRef, newImageFile);
      const url = await getDownloadURL(storageRef);
      const imageObj = {
        url,
        path: imagePath,
        primary: false,
        alt: `${editableVehicle.brand} ${editableVehicle.model}`,
      };
      const refDoc = doc(
        firestore,
        "rentalShops",
        shopSlug,
        "vehicles",
        vehicleId
      );
      await updateDoc(refDoc, {
        images: arrayUnion(imageObj),
      });
      setVehicle((prev) => ({ ...prev, images: [...prev.images, imageObj] }));
    } catch (err) {
      console.error("Upload error:", err);
    } finally {
      setUploading(false);
      setNewImageFile(null);
    }
  };

  const handleDeleteImage = async (image) => {
    if (!window.confirm("Delete image?")) return;
    try {
      await deleteObject(ref(storage, image.path));
      const refDoc = doc(
        firestore,
        "rentalShops",
        shopSlug,
        "vehicles",
        vehicleId
      );
      await updateDoc(refDoc, {
        images: arrayRemove(image),
      });
      setVehicle((prev) => ({
        ...prev,
        images: prev.images.filter((img) => img.path !== image.path),
      }));
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  // When thumbnail is clicked, show full-size popup
  const handleThumbnailClick = (url) => {
    setSelectedImageUrl(url);
    setOpenImageDialog(true);
  };

  const handleCloseImageDialog = () => {
    setOpenImageDialog(false);
    setSelectedImageUrl("");
  };

  // --- New: Fetch bookings for this vehicle ---
  useEffect(() => {
    if (!shopSlug || !vehicleId) return;
    const fetchBookings = async () => {
      try {
        const bookingsQuery = await getDocs(
          query(
            collection(firestore, "rentalShops", shopSlug, "bookings"),
            where("vehicle.vehicleId", "==", vehicleId)
          )
        );
        const bookingsList = bookingsQuery.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setBookings(bookingsList);
        console.log("bookingsList: ", bookingsList);
      } catch (error) {
        console.error("Error fetching bookings:", error);
      }
    };
    fetchBookings();
  }, [shopSlug, vehicleId]);

  if (loadingAuth || !shopSlug) {
    return (
      <Container maxWidth="lg" sx={{ textAlign: "center", mt: 10 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading authentication...</Typography>
      </Container>
    );
  }

  if (!vehicle) {
    return (
      <Container maxWidth="lg" sx={{ textAlign: "center", mt: 10 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading vehicle data...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      {/* Top Navigation: Back to Inventory */}
      <Box sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate("/inventory")}>
          <ArrowBackIosIcon /> Back
        </IconButton>
      </Box>

      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h4" align="left" gutterBottom>
          Vehicle Details
        </Typography>

        {/* Main Vehicle Information */}
        {editMode ? (
          <Box component="form" sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={editableVehicle.flags?.active ?? true}
                  onChange={(e) =>
                    setEditableVehicle((prev) => ({
                      ...prev,
                      flags: { ...prev.flags, active: e.target.checked },
                    }))
                  }
                />
              }
              label="Vehicle is Active"
            />
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Basic fields */}
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Plate Number"
                  name="plateNr"
                  value={editableVehicle.plateNr || ""}
                  onChange={handleEditableChange}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Brand"
                  name="brand"
                  value={editableVehicle.brand || ""}
                  onChange={handleEditableChange}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Model"
                  name="model"
                  value={editableVehicle.model || ""}
                  onChange={handleEditableChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={editableVehicle.description || ""}
                  onChange={handleEditableChange}
                  multiline
                  rows={3}
                />
              </Grid>

              {/* Pricing Editing Section */}
              <Grid item xs={12}>
                <Typography variant="h6">Pricing</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      required
                      label="Base Daily Rate"
                      name="daily"
                      value={editableVehicle.pricing?.daily || ""}
                      onChange={handlePricingChange}
                      type="number"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Rate for 1 Week+ (optional)"
                      name="oneWeek"
                      value={editableVehicle.pricing?.oneWeek || ""}
                      onChange={handlePricingChange}
                      type="number"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Rate for 2 Weeks+ (optional)"
                      name="twoWeeks"
                      value={editableVehicle.pricing?.twoWeeks || ""}
                      onChange={handlePricingChange}
                      type="number"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Rate for 1 Month+ (optional)"
                      name="oneMonth"
                      value={editableVehicle.pricing?.oneMonth || ""}
                      onChange={handlePricingChange}
                      type="number"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Extra Hours Rate"
                      name="extraHours"
                      type="number"
                      value={editableVehicle.pricing?.extraHours || ""}
                      onChange={handlePricingChange}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Deposit */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Deposit"
                  name="deposit"
                  type="number"
                  value={editableVehicle.deposit || ""}
                  onChange={handleEditableChange}
                />
              </Grid>

              {/* Features Section */}
              <Grid item xs={12}>
                <Typography variant="h6">Features</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Engine Size"
                  name="engineSize"
                  value={editableVehicle.features?.engineSize || ""}
                  onChange={(e) =>
                    setEditableVehicle((prev) => ({
                      ...prev,
                      features: {
                        ...prev.features,
                        engineSize: e.target.value,
                      },
                    }))
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Fuel Type"
                  name="fuelType"
                  value={editableVehicle.features?.fuelType || ""}
                  onChange={(e) =>
                    setEditableVehicle((prev) => ({
                      ...prev,
                      features: {
                        ...prev.features,
                        fuelType: e.target.value,
                      },
                    }))
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Seats"
                  name="seats"
                  type="number"
                  value={editableVehicle.features?.seats ?? 2}
                  onChange={(e) =>
                    setEditableVehicle((prev) => ({
                      ...prev,
                      features: {
                        ...prev.features,
                        seats: Number(e.target.value),
                      },
                    }))
                  }
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Transmission"
                  name="transmission"
                  value={editableVehicle.features?.transmission || ""}
                  onChange={(e) =>
                    setEditableVehicle((prev) => ({
                      ...prev,
                      features: {
                        ...prev.features,
                        transmission: e.target.value,
                      },
                    }))
                  }
                  fullWidth
                  SelectProps={{ native: true }}
                >
                  <option value="">-- Select --</option>
                  <option value="Automatic">Automatic</option>
                  <option value="Manual">Manual</option>
                </TextField>
              </Grid>

              {/* Extras Editing Section */}
              <Grid item xs={12}>
                <Typography variant="h6">Extras</Typography>
                {editableVehicle.extras && editableVehicle.extras.length > 0 ? (
                  editableVehicle.extras.map((extra, index) => (
                    <Box key={index} sx={{ display: "flex", gap: 2, mb: 1 }}>
                      <TextField
                        label="Extra Name"
                        value={extra.name}
                        onChange={(e) =>
                          handleExtraChange(index, "name", e.target.value)
                        }
                      />
                      <TextField
                        label="Price"
                        type="number"
                        value={extra.price}
                        onChange={(e) =>
                          handleExtraChange(index, "price", e.target.value)
                        }
                      />
                      <IconButton
                        onClick={() => handleDeleteExtra(index)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))
                ) : (
                  <Typography>No extras added.</Typography>
                )}
                <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                  <TextField
                    label="New Extra Name"
                    value={newExtraName}
                    onChange={(e) => setNewExtraName(e.target.value)}
                  />
                  <TextField
                    label="Price"
                    type="number"
                    value={newExtraPrice}
                    onChange={(e) => setNewExtraPrice(e.target.value)}
                  />
                  <IconButton onClick={handleAddExtra} color="primary">
                    <AddIcon />
                  </IconButton>
                </Box>
              </Grid>
            </Grid>
            <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
              <Button variant="contained" color="primary" onClick={handleSave}>
                Save
              </Button>
              <Button variant="outlined" onClick={handleEditToggle}>
                Cancel
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ mb: 2 }}>
            {vehicle.flags?.active ? (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: "green",
                    mr: 1,
                  }}
                />
                <Typography variant="h6" color="green">
                  Active
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: "red",
                    mr: 1,
                  }}
                />
                <Typography variant="h6" color="red">
                  Not Active
                </Typography>
              </Box>
            )}
            <Typography variant="h6">Vehicle type: {vehicle.type}</Typography>
            <Typography variant="h6">
              Plate Number: {vehicle.plateNr}
            </Typography>
            <Typography variant="h6">Brand: {vehicle.brand}</Typography>
            <Typography variant="h6">Model: {vehicle.model}</Typography>
            <Typography variant="body1">
              Description: {vehicle.description}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Typography variant="body1" fontWeight="bold">
                Rental Rates:
              </Typography>
              <Typography variant="body1">
                Base Daily Rate: ₱{Number(vehicle.pricing?.daily).toFixed(2)}
              </Typography>
              {vehicle.pricing?.oneWeek && (
                <Typography variant="body1">
                  1 Week+ Rate: ₱{Number(vehicle.pricing?.oneWeek).toFixed(2)}{" "}
                  per day
                </Typography>
              )}
              {vehicle.pricing?.twoWeeks && (
                <Typography variant="body1">
                  2 Weeks+ Rate: ₱{Number(vehicle.pricing?.twoWeeks).toFixed(2)}{" "}
                  per day
                </Typography>
              )}
              {vehicle.pricing?.oneMonth && (
                <Typography variant="body1">
                  1 Month+ Rate: ₱{Number(vehicle.pricing?.oneMonth).toFixed(2)}{" "}
                  per day
                </Typography>
              )}
              {vehicle.pricing?.extraHours && (
                <Typography variant="body1">
                  Extra Hours Rate: ₱
                  {Number(vehicle.pricing?.extraHours).toFixed(2)} per hour
                </Typography>
              )}
            </Box>
            <Typography variant="body1">Deposit: ₱{vehicle.deposit}</Typography>

            {vehicle.features && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6">Features:</Typography>
                {vehicle.features.engineSize && (
                  <Typography>
                    Engine Size: {vehicle.features.engineSize}
                  </Typography>
                )}
                {vehicle.features.fuelType && (
                  <Typography>
                    Fuel Type: {vehicle.features.fuelType}
                  </Typography>
                )}
                <Typography>Seats: {vehicle.features.seats || 2}</Typography>
                {vehicle.features.transmission && (
                  <Typography>
                    Transmission: {vehicle.features.transmission}
                  </Typography>
                )}
              </Box>
            )}

            {vehicle.extras && vehicle.extras.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6">Extras:</Typography>
                {vehicle.extras.map((extra, i) => (
                  <Typography key={i} variant="body1">
                    {extra.name}: ₱{extra.price}
                  </Typography>
                ))}
              </Box>
            )}
            <Button
              variant="contained"
              onClick={handleEditToggle}
              sx={{ mt: 2 }}
            >
              Edit Details
            </Button>
            <Button
              variant="outlined"
              color="error"
              sx={{ mt: 2, ml: 2 }}
              onClick={handleDeleteVehicleClick}
            >
              Delete Vehicle
            </Button>
          </Box>
        )}

        {/* Vehicle Images Section */}
        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            Vehicle Images
          </Typography>
          {vehicle.images && vehicle.images.length > 0 ? (
            <Stack direction="row" spacing={2} flexWrap="wrap">
              {vehicle.images.map((image, index) => (
                <Box
                  key={index}
                  sx={{
                    position: "relative",
                    display: "inline-block",
                    cursor: "pointer",
                  }}
                  onClick={() => handleThumbnailClick(image.url)}
                >
                  <CardMedia
                    component="img"
                    image={image.url}
                    alt={`Vehicle Image ${index + 1}`}
                    sx={{
                      width: 150,
                      height: 100,
                      objectFit: "cover",
                    }}
                  />
                  <IconButton
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      backgroundColor: "rgba(255,255,255,0.7)",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteImage(image);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography>No images available.</Typography>
          )}
          <Box sx={{ mt: 2 }}>
            <TextField
              type="file"
              inputProps={{ accept: "image/*" }}
              onChange={handleImageFileChange}
            />
            <Button
              variant="contained"
              color="primary"
              sx={{ ml: 2 }}
              onClick={handleUploadImage}
              disabled={uploading || !newImageFile}
            >
              {uploading ? "Uploading..." : "Upload Image"}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* --- New: Bookings Section --- */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Bookings
        </Typography>
        {bookings.length > 0 ? (
          <Stack spacing={1}>
            {bookings.map((booking, idx) => (
              <Paper
                key={idx}
                sx={{ p: 1, cursor: "pointer" }}
                onClick={() => navigate(`/booking/${booking.id}`)}
              >
                <Typography variant="body2">
                  {booking.title} | {moment(booking.startDate).format("LLL")} -{" "}
                  {moment(booking.endDate).format("LLL")}
                </Typography>
              </Paper>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2">No bookings found.</Typography>
        )}

        <Box sx={{ mt: 2 }}>
          <VehicleCalendar availability={vehicle.availability} />
        </Box>
      </Paper>

      {/* Image Popup Dialog */}
      <Dialog
        open={openImageDialog}
        onClose={handleCloseImageDialog}
        maxWidth="lg"
      >
        <DialogTitle>Full-Size Image</DialogTitle>
        <DialogContent>
          <CardMedia
            component="img"
            image={selectedImageUrl}
            alt="Full Size Vehicle"
            sx={{ width: "100%", maxHeight: "80vh", objectFit: "contain" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImageDialog}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Vehicle</DialogTitle>
        <DialogContent>
          <Typography>
            This vehicle has no bookings. Do you want to:
            <br />
            <strong>1. Permanently delete the vehicle</strong>
            <br />
            <strong>2. Set it as inactive instead</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              // Set inactive
              await updateDoc(
                doc(firestore, "rentalShops", shopSlug, "vehicles", vehicleId),
                { flags: { ...vehicle.flags, active: false } }
              );
              setVehicle((prev) => ({
                ...prev,
                flags: { ...prev.flags, active: false },
              }));
              setDeleteDialogOpen(false);
            }}
          >
            Set Inactive
          </Button>
          <Button
            onClick={async () => {
              await deleteObject(
                ref(storage, `vehicleImages/${shopSlug}/${vehicleId}`)
              ).catch(() => {});
              await updateDoc(
                doc(firestore, "rentalShops", shopSlug, "vehicles", vehicleId),
                { images: [] }
              );
              await deleteDoc(
                doc(firestore, "rentalShops", shopSlug, "vehicles", vehicleId)
              );
              setDeleteDialogOpen(false);
              navigate("/inventory");
            }}
            color="error"
          >
            Delete Vehicle
          </Button>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default VehicleDetail;
